import React, { SetStateAction, useEffect, useState } from "react";
import { ColumnDetail } from "@avamae/table";
import { Cell, CellOptions } from "./Cell";
import { makeRow, Checkbox, FlexProps } from "./FlexTable";
import { useDetectOverflow } from "helpers/useDetectOverflow";
import { lowerCaseFirstLetter } from "helpers/lowerCaseFirstLetter";
import { TCustomCell } from "components/Category/Category";
import { ScrollSyncPane } from "react-scroll-sync";

type MiddleTableProps<T = any> = {
  idColumn: string;
  columns: ColumnDetail<any>[];
  columnData: any[];
  rowHovered: number | null;
  handleMouseEnterRow(e: any, i: number): void;
  onRowClick?(id: number): void;
  checkbox?: Checkbox;
  rowButton?(data: any): React.ReactNode;
  hiddenColumns?: string[];
  formatPrimitives(value: unknown): any;
  rowFormatter?(r: T): { [K in keyof T]: React.ReactNode };
  noRowHover?: boolean;
  rowCellOptions?: { [K in keyof Partial<T>]: CellOptions };
  toggleColumnSort?: (k: string) => void;
  sortBy: { [k: string]: "ASC" | "DESC" };
  setOverflow: React.Dispatch<SetStateAction<boolean>>;
  disableMultiSelect?: boolean;
  customRowCells?: {
    [k in keyof T]?: TCustomCell<T>;
  };

  setCustomCellValue?: React.Dispatch<any>;
  customCellValue?: any;
  onDoubleClick?: () => void;

  refs?: { selectRef?: any; containerRef?: any };
  parentSelectedId?: number;
  showRightTable?: boolean;
};

const HEADER_LABEL_FONT_SIZE = 11; // pixels / 1.1rem
const HEADER_LABEL_MIN_WIDTH = 100; // pixels

export type ColumnAutoWidths = {
  [columnKey: string]: number;
};

const MiddleTable: React.FC<MiddleTableProps & FlexProps> = ({
  idColumn,
  columns,
  columnData,
  rowHovered,
  handleMouseEnterRow,
  checkbox,
  onRowClick,
  rowButton,
  hiddenColumns,
  formatPrimitives,
  rowFormatter,
  noRowHover,
  rowCellOptions = {},
  toggleColumnSort,
  sortBy,
  setOverflow,
  disableMultiSelect,
  customRowCells,
  setCustomCellValue,
  customCellValue,
  menuActionsCode,
  setMenuActionsCode,
  onDoubleClick,
  twoSingleClicks,
  refs,
  parentSelectedId,
  showRightTable,
}) => {
  const { ref, hasOverflow } = useDetectOverflow([checkbox?.selectedIds]);

  const [columnAutoWidths, setColumnAutoWidths] = useState<
    ColumnAutoWidths | undefined
  >(undefined);

  useEffect(() => {
    setOverflow(hasOverflow);
  }, [hasOverflow]);
  useEffect(() => {
    let tempColumnAutoWidths: ColumnAutoWidths = {};
    columns.forEach((column: ColumnDetail) => {
      if (typeof column.columnKey === "string") {
        const parsedColumnName = lowerCaseFirstLetter(column.columnKey);

        if (!rowCellOptions.hasOwnProperty(parsedColumnName)) {
          const textLength = column.labelValue.length;
          const columnWidth = Math.ceil(
            (HEADER_LABEL_FONT_SIZE + 3) * textLength
          ); // +1 to account for capital letters and different letter widths

          if (columnWidth > HEADER_LABEL_MIN_WIDTH) {
            if (columnWidth >= 250) {
              tempColumnAutoWidths[parsedColumnName] = columnWidth * 0.8;
            } else if (columnWidth >= 200) {
              tempColumnAutoWidths[parsedColumnName] = columnWidth * 0.8;
            } else {
              tempColumnAutoWidths[parsedColumnName] = columnWidth;
            }
          }
        }
      }
    });

    if (Object.keys(tempColumnAutoWidths).length === 0) {
      setColumnAutoWidths(undefined);
    } else {
      setColumnAutoWidths(tempColumnAutoWidths);
    }
  }, [columns, rowCellOptions]);

  const leftTableWidth =
    document.getElementById(`LeftSideTableContainer-${idColumn}`)
      ?.offsetWidth ?? 0;
  const middleTableHeadHeight =
    document.getElementById(`MiddleTableHead-${idColumn}`)?.offsetHeight ?? 0;

  const RightTableHeadWidth =
    document.getElementById(`RightTableHead-${idColumn}`)?.offsetWidth ?? 0;

  const middleHeader = document.getElementById(`MiddleTableHead-${idColumn}`);

  useEffect(() => {
    if (columnData && columnData.length === 0) {
      if (middleHeader) {
        // NOTE: Could not use ref as it conflicts with the <ScrollSyncPane>
        middleHeader.scrollLeft = 0;
      }
    }
  }, [columnData]);

  return (
    <div
      id="MiddleTable_OverflowEnabled"
      ref={ref}
      className={`MiddleTable ${hasOverflow ? " BoxShadow" : ""} ${
        columnData && Array.isArray(columnData) && columnData.length === 0
          ? " NoListDataNoScrollbar"
          : ""
      } ${showRightTable ? "ShowRightTable" : ""}`}
      style={{ paddingTop: `calc(30px + ${middleTableHeadHeight}px)` }}
    >
      <ScrollSyncPane>
        <div
          className="Head"
          style={{
            left: `${leftTableWidth}px`,
            minWidth: !showRightTable
              ? `calc(100% - ${leftTableWidth}px)`
              : `calc(100% - ${leftTableWidth}px - ${RightTableHeadWidth}px)`,
            maxWidth: !showRightTable
              ? `calc(100% - ${leftTableWidth}px)`
              : `calc(100% - ${leftTableWidth}px - ${RightTableHeadWidth}px)`,
          }}
          id={`MiddleTableHead-${idColumn}`}
        >
          {columns
            .filter(
              c =>
                !hiddenColumns?.includes(c.columnKey.toString().toLowerCase())
            )
            .map((c, i) => {
              const sortByValue = sortBy[String(c.columnKey)] ?? "NONE";
              const isSorting = sortByValue === "ASC" || sortByValue === "DESC";
              const { bSortable } = c;

              const columnKey = lowerCaseFirstLetter(String(c.columnKey));

              const options = rowCellOptions[columnKey];

              const autoWidth =
                columnAutoWidths && columnAutoWidths.hasOwnProperty(columnKey)
                  ? columnAutoWidths[columnKey]
                  : undefined;

              return (
                <Cell
                  key={`middle-table-cell-${i}`}
                  options={options}
                  sortable={bSortable}
                  onClick={() =>
                    bSortable &&
                    toggleColumnSort &&
                    toggleColumnSort(String(c.columnKey))
                  }
                  tooltipLabel={c.labelValue}
                  overrideWidth={autoWidth}
                >
                  <span
                    key={`middle-table-cell-span-${i}`}
                    className={bSortable && isSorting ? "Sorting" : ""}
                  >
                    {c.labelValue}
                  </span>
                  {bSortable && (
                    <i
                      className={
                        sortByValue === "ASC"
                          ? "SortIcon ASC"
                          : sortByValue === "DESC"
                          ? "SortIcon DESC"
                          : "SortIcon"
                      }
                      key={`middle-table-cell-i-${i}`}
                    />
                  )}
                </Cell>
              );
            })}
          {rowButton && (
            <Cell
              options={{ noSortIcon: true }}
              key={`row-button-${idColumn}`}
            />
          )}
        </div>
      </ScrollSyncPane>

      <ScrollSyncPane>
        <div
          className="TableBody"
          style={{
            left: `${leftTableWidth}px`,
            minWidth: !showRightTable
              ? `calc(100% - ${leftTableWidth}px)`
              : `calc(100% - ${leftTableWidth}px - 60px)`,
            maxWidth: !showRightTable
              ? `calc(100% - ${leftTableWidth}px)`
              : `calc(100% - ${leftTableWidth}px - 60px)`,
            marginBottom: "-10px",
          }}
        >
          {columnData.map((d, i) =>
            makeRow({
              id: d[idColumn].toString(),
              r: d,
              index: i,
              columns,
              formatPrimitives,
              hiddenColumns: hiddenColumns ?? [],
              rowHovered,
              handleMouseEnterRow,
              onRowClick: onRowClick ? () => onRowClick(d[idColumn]) : null,
              rowButton: rowButton ? () => rowButton(d) : undefined,
              formatRow: rowFormatter,
              checkbox,
              noRowHover,
              rowSelected:
                checkbox?.selectedIds.includes(d[idColumn]) ||
                checkbox?.selectedIds.includes(d[idColumn].toString()) ||
                !!checkbox?.selectedIds.includes(parseInt(d[idColumn], 10)), //!!checkbox?.selectedIds.find(ids => ids.id === parseInt(d[idColumn], 10)),
              hideRowOptionComponent: checkbox
                ? checkbox.selectedIds.length > 0
                : false,
              rowCellOptions,
              disableMultiSelect,
              customRowCells,
              setCustomCellValue,
              customCellValue,
              menuActionsCode,
              setMenuActionsCode,
              onDoubleClick,
              columnAutoWidths,
              twoSingleClicks,
              position: "Middle",
              refs,
              parentSelectedId,
            })
          )}
        </div>
      </ScrollSyncPane>
    </div>
  );
};

export { MiddleTable };
